/**
 * Conversation list + thread reads from the unified `conversations/{convId}`
 * schema. See `message.service.js` for the schema overview.
 */

import { auth, ref } from '../../core/firebase.js';
import { createLogger } from '../../core/logger.js';
import * as bus from '../../core/eventBus.js';
import { conversationKey } from './message.service.js';
import { normalize as normalizePlate } from '../../shared/plate.js';
import { LocalState } from '../../core/localState.js';

const log = createLogger('conversation');

/**
 * @typedef {Object} Message
 * @property {string} id
 * @property {string} from
 * @property {string} body
 * @property {number} sentAt
 * @property {boolean} [smsSent]
 * @property {boolean} mine
 *
 * @typedef {Object} ConversationSummary
 * @property {string} convId
 * @property {string} peerPlate
 * @property {string} [peerUid]
 * @property {{ from: string; body: string; sentAt: number }} lastMessage
 * @property {boolean} mineLast
 * @property {number} unread
 * @property {number} updatedAt
 */

/**
 * Decide whether an inbox entry should be visible. An entry is hidden if the
 * user soft-deleted the conversation (`clearedAt` set) AND no new message
 * has arrived since (`updatedAt <= clearedAt`). When a new message comes in,
 * the fan-out bumps `updatedAt > clearedAt` so the conversation reappears
 * — but only the new messages are visible (the thread view filters by the
 * same `clearedAt`).
 *
 * @param {any} v   the entry value from /userConversations/{uid}/{convId}
 * @returns {boolean}
 */
function isVisible(v) {
  const clearedAt = Number(v?.clearedAt ?? 0);
  if (!clearedAt) return true;
  const updatedAt = Number(v?.updatedAt ?? 0);
  return updatedAt > clearedAt;
}

/**
 * Fetch all conversations for the current user, newest first.
 * @returns {Promise<ConversationSummary[]>}
 */
export async function list() {
  const uid = auth().currentUser?.uid;
  if (!uid) return [];
  const snap = await ref(`userConversations/${uid}`)
    .orderByChild('updatedAt')
    .limitToLast(200)
    .once('value');
  /** @type {ConversationSummary[]} */
  const rows = [];
  snap.forEach((/** @type {any} */ c) => {
    const v = c.val();
    if (!v || !isVisible(v)) return;
    rows.push({
      convId: c.key,
      peerPlate: v.peerPlate ?? '',
      peerUid: v.peerUid ?? undefined,
      lastMessage: v.lastMessage ?? { from: '', body: '', sentAt: 0 },
      mineLast: !!v.mineLast,
      unread: v.unreadCount || 0,
      updatedAt: v.updatedAt || 0,
    });
  });
  return rows.sort((a, b) => b.updatedAt - a.updatedAt);
}

/**
 * Fetch the full thread between me and a peer plate, ordered oldest-first.
 * Messages older than the user's per-conversation `clearedAt` (set by a
 * soft-delete) are filtered out — so a previously-cleared thread shows
 * nothing until the OTHER side sends a new message.
 *
 * @param {string} peerPlate
 * @returns {Promise<Message[]>}
 */
export async function getThread(peerPlate) {
  const uid = auth().currentUser?.uid;
  const myPlate = normalizePlate(LocalState.plate ?? '');
  const peer = normalizePlate(peerPlate);
  if (!uid || !peer || !myPlate) return [];

  const convId = conversationKey(myPlate, peer);
  const clearedAt = await readClearedAt(uid, convId);
  const snap = await ref(`conversations/${convId}/messages`)
    .orderByChild('sentAt')
    .limitToLast(500)
    .once('value');
  /** @type {Message[]} */
  const rows = [];
  snap.forEach((/** @type {any} */ m) => {
    const v = m.val();
    if (!v) return;
    if ((v.sentAt ?? 0) <= clearedAt) return;
    rows.push({
      id: m.key,
      from: v.from ?? '',
      body: v.body ?? '',
      sentAt: v.sentAt ?? 0,
      smsSent: !!v.smsSent,
      mine: v.from === uid,
    });
  });
  return rows.sort((a, b) => (a.sentAt ?? 0) - (b.sentAt ?? 0));
}

/**
 * @param {string} uid
 * @param {string} convId
 * @returns {Promise<number>}  millis timestamp; 0 if never cleared.
 */
async function readClearedAt(uid, convId) {
  try {
    const snap = await ref(`userConversations/${uid}/${convId}/clearedAt`).once('value');
    const n = Number(snap.val() ?? 0);
    return Number.isFinite(n) ? n : 0;
  } catch { return 0; }
}

/**
 * Subscribe to live updates on the inbox (the current user's conversation
 * list). Fires whenever any conversation summary changes.
 *
 * @param {(rows: ConversationSummary[]) => void} onChange
 * @returns {() => void}
 */
export function subscribeInbox(onChange) {
  const uid = auth().currentUser?.uid;
  if (!uid) return () => {};
  const r = ref(`userConversations/${uid}`)
    .orderByChild('updatedAt')
    .limitToLast(200);
  const cb = (/** @type {any} */ snap) => {
    /** @type {ConversationSummary[]} */
    const rows = [];
    snap.forEach((/** @type {any} */ c) => {
      const v = c.val();
      if (!v || !isVisible(v)) return;
      rows.push({
        convId: c.key,
        peerPlate: v.peerPlate ?? '',
        peerUid: v.peerUid ?? undefined,
        lastMessage: v.lastMessage ?? { from: '', body: '', sentAt: 0 },
        mineLast: !!v.mineLast,
        unread: v.unreadCount || 0,
        updatedAt: v.updatedAt || 0,
      });
    });
    rows.sort((a, b) => b.updatedAt - a.updatedAt);
    const totalUnread = rows.reduce((sum, r) => sum + r.unread, 0);
    bus.emit('conversation:unread', { convId: 'inbox', unread: totalUnread });
    try { onChange(rows); }
    catch (e) { log.warn('inbox handler threw:', e); }
  };
  r.on('value', cb, (/** @type {Error} */ err) => log.warn('inbox listener:', err.message));
  return () => { try { r.off('value', cb); } catch { /* ignore */ } };
}

/**
 * Soft-delete a conversation from the current user's inbox.
 *
 * Sets `clearedAt = now` on the user's `userConversations` entry instead of
 * removing it. The entry stays so we can keep the `clearedAt` marker, but
 * `isVisible()` hides it from the inbox until a NEW message arrives
 * (which bumps `updatedAt > clearedAt` and brings the conversation back).
 * The thread view filters out messages older than `clearedAt`, so even when
 * the conversation reappears the previously-deleted history is gone for
 * this user.
 *
 * The other party is untouched: their `userConversations/{otherUid}/{convId}`
 * has no `clearedAt` change, and the `/conversations/{convId}/messages`
 * remain on the server so their thread continues to display normally.
 *
 * @param {string} convId
 * @returns {Promise<void>}
 */
export async function deleteFromInbox(convId) {
  const uid = auth().currentUser?.uid;
  if (!uid || !convId) return;
  try {
    const now = Date.now();
    await ref(`userConversations/${uid}/${convId}`).update({
      clearedAt: now,
      unreadCount: 0,
      lastMessage: { from: '', body: '', sentAt: 0 },
    });
  } catch (err) {
    log.warn('deleteFromInbox failed:', err);
    throw err;
  }
}

/**
 * Subscribe to a single thread's updates. Returns unsubscribe.
 *
 * If `explicitConvId` is supplied (preferred), that conversation is used
 * directly — important after a plate change, where computing the convId
 * from the current LocalState.plate would yield a different (empty) thread.
 * If not supplied, falls back to deriving from the current plate.
 *
 * @param {string} peerPlate
 * @param {(messages: Message[]) => void} onChange
 * @param {string} [explicitConvId]
 * @returns {() => void}
 */
export function subscribeThread(peerPlate, onChange, explicitConvId) {
  const uid = auth().currentUser?.uid;
  const peer = normalizePlate(peerPlate);
  if (!uid) return () => {};
  // Admin/system threads have no peer plate, just a fixed `admin_${uid}`
  // convId. Accept that case by requiring EITHER a peer plate OR an
  // explicit convId.
  if (!peer && !explicitConvId) return () => {};

  let convId = explicitConvId || '';
  if (!convId) {
    const myPlate = normalizePlate(LocalState.plate ?? '');
    if (!myPlate) return () => {};
    convId = conversationKey(myPlate, peer);
  }

  // Look up the user's per-conversation clearedAt once; we use it to filter
  // out messages from before the soft-delete. We re-read it on each snapshot
  // would be ideal, but a one-time read is fine because clearedAt only
  // changes when the user manually deletes (which already triggers a
  // re-subscribe at that point).
  /** @type {number} */
  let clearedAt = 0;
  ref(`userConversations/${uid}/${convId}/clearedAt`).once('value')
    .then((s) => { clearedAt = Number(s.val() ?? 0) || 0; })
    .catch(() => { /* ignore */ });

  const r = ref(`conversations/${convId}/messages`).orderByChild('sentAt').limitToLast(500);
  const cb = (/** @type {any} */ snap) => {
    /** @type {Message[]} */
    const rows = [];
    snap.forEach((/** @type {any} */ m) => {
      const v = m.val();
      if (!v) return;
      if ((v.sentAt ?? 0) <= clearedAt) return;
      rows.push({
        id: m.key,
        from: v.from ?? '',
        body: v.body ?? '',
        sentAt: v.sentAt ?? 0,
        smsSent: !!v.smsSent,
        mine: v.from === uid,
      });
    });
    rows.sort((a, b) => (a.sentAt ?? 0) - (b.sentAt ?? 0));
    try { onChange(rows); }
    catch (e) { log.warn('thread handler threw:', e); }
  };
  r.on('value', cb, (/** @type {Error} */ err) => log.warn('thread listener:', err.message));
  return () => { try { r.off('value', cb); } catch { /* ignore */ } };
}
